import React, { useState, useEffect, useCallback, useRef } from 'react';
import { serverPHP } from '../const/consts';
import { useNavigate } from 'react-router-dom';
import SessionTile from '../composants/SessionTile';
import ErrorHandler from '../composants/ErrorHandler';

const SessionSelector = React.memo(({ user, setUser }) => {
  const [sessions, setSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showClosed, setShowClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const navigate = useNavigate();
  const previousSessionsRef = useRef([]);
  const isUnmountedRef = useRef(false);

  const fetchSessions = useCallback(async () => {
    if (isUnmountedRef.current) return;

    try {
      setError(null);
      
      const timeoutId = setTimeout(() => {
        throw new Error('TIMEOUT');
      }, 10000);

      const response = await fetch(serverPHP + 'getAllSessions.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error('SERVER_ERROR');
        } else if (response.status === 404) {
          throw new Error('ENDPOINT_NOT_FOUND');
        } else {
          throw new Error(`HTTP_ERROR_${response.status}`);
        }
      }

      const data = await response.json();
      
      if (data.success === true && Array.isArray(data.sessions)) {
        const newSessions = data.sessions;
        if (JSON.stringify(newSessions) !== JSON.stringify(previousSessionsRef.current)) {
          setSessions(newSessions);
          previousSessionsRef.current = newSessions;
        }
        setRetryCount(0);
      } else if (data.success === false && Array.isArray(data.sessions)) {
        console.warn('Server returned success:false but with sessions array, treating as empty result');
        setSessions([]);
        previousSessionsRef.current = [];
        setRetryCount(0);
      } else if (data.success === false && data.error) {
        throw new Error('SERVER_ERROR');
      } else {
        console.error('Unexpected server response:', data);
        throw new Error('INVALID_RESPONSE');
      }
      
      setLoading(false);
      
    } catch (error) {
      console.error('Erreur:', error);
      
      if (isUnmountedRef.current) return;
      
      let errorDetails = {
        type: 'UNKNOWN_ERROR',
        message: 'Erreur lors du chargement des sessions'
      };

      switch (error.message) {
        case 'TIMEOUT':
          errorDetails = {
            type: 'TIMEOUT',
            message: 'Le chargement des sessions prend trop de temps'
          };
          break;
        case 'SERVER_ERROR':
          errorDetails = {
            type: 'SERVER_ERROR',
            message: 'Erreur serveur lors du chargement des sessions'
          };
          break;
        case 'ENDPOINT_NOT_FOUND':
          errorDetails = {
            type: 'ENDPOINT_NOT_FOUND',
            message: 'Service de sessions non disponible'
          };
          break;
        case 'INVALID_RESPONSE':
          errorDetails = {
            type: 'DATA_ERROR',
            message: 'Données de sessions invalides reçues du serveur'
          };
          break;
        default:
          if (error.name === 'TypeError' && error.message.includes('fetch')) {
            errorDetails = {
              type: 'NETWORK_ERROR',
              message: 'Impossible de charger les sessions'
            };
          }
      }
      
      setError(errorDetails);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  const handleSessionClick = useCallback((sessionId) => {
    navigate("/session/" + sessionId);
  }, [navigate]);

  useEffect(() => {
    fetchSessions();
    
    let intervalId;
    if (!error && !isUnmountedRef.current) {
      intervalId = setInterval(() => {
        if (!error && !isUnmountedRef.current) {
          fetchSessions();
        }
      }, 5000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchSessions, error]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleShowClosedChange = useCallback(() => {
    setShowClosed(prev => !prev);
    setCurrentPage(1);
  }, []);

  const handleRetry = useCallback(() => {
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setLoading(true);
      setError(null);
      
      const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
      setTimeout(() => {
        if (!isUnmountedRef.current) {
          fetchSessions();
        }
      }, delay);
    } else {
      navigate('/');
    }
  }, [retryCount, maxRetries, navigate, fetchSessions]);

  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const sessionsPerPage = 9;
  const filteredSessions = sessions.filter(session =>
    (showClosed ? session.closed : !session.closed) &&
    (session.sujet.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.session_id.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  const totalPages = Math.ceil(filteredSessions.length / sessionsPerPage);

  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;

    const addPageButton = (page) => {
      pages.push(
        <button
          key={page}
          onClick={() => setCurrentPage(page)}
          className={`pagination-btn ${page === currentPage ? 'active' : ''}`}
        >
          {page}
        </button>
      );
    };

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        addPageButton(i);
      }
    } else {
      addPageButton(1);

      if (currentPage > 3) {
        pages.push(<span key="start-ellipsis" className="pagination-ellipsis">...</span>);
      }

      let startPage = Math.max(currentPage - 1, 2);
      let endPage = Math.min(currentPage + 1, totalPages - 1);

      for (let i = startPage; i <= endPage; i++) {
        addPageButton(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push(<span key="end-ellipsis" className="pagination-ellipsis">...</span>);
      }

      addPageButton(totalPages);
    }

    return (
      <div className="pagination">
        {pages}
      </div>
    );
  };

  const startIndex = (currentPage - 1) * sessionsPerPage;
  const paginatedSessions = filteredSessions.slice(startIndex, startIndex + sessionsPerPage);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        onRetry={retryCount < maxRetries ? handleRetry : null}
        onGoHome={handleGoHome}
        showRetry={retryCount < maxRetries}
        customActions={retryCount >= maxRetries ? [
          { text: '🏠 Retour à l\'accueil', action: handleGoHome, primary: true },
          { text: '✨ Créer une session', action: () => navigate('/nouvellesession'), secondary: true }
        ] : null}
      />
    );
  }

  if (loading) {
    return <></>;
  }

  return (
    <div className="fade-in">
      <div className="card">
        <h2 className="card-title">🎯 Rejoindre une session</h2>
        
        <div className="session-controls">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="🔍 Rechercher par code de session ou titre..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="filter-container">
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={showClosed}
                onChange={handleShowClosedChange}
              />
              <span className="filter-text">📋 Inclure les sessions terminées</span>
            </label>
          </div>
        </div>

        {searchQuery && filteredSessions.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3 className="empty-title">Aucune session trouvée</h3>
            <p className="empty-description">
              Aucune session ne correspond à "<strong>{searchQuery}</strong>"
            </p>
            <div className="mt-4">
              <button className="btn btn-secondary" onClick={() => setSearchQuery('')}>
                🗑️ Effacer la recherche
              </button>
            </div>
          </div>
        )}

        {!searchQuery && sessions.length === 0 && !loading && (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <h3 className="empty-title">Aucune session disponible</h3>
            <p className="empty-description">Soyez le premier à créer une session collaborative !</p>
            <div className="mt-4">
              <button className="btn btn-primary" onClick={() => navigate('/nouvellesession')}>
                ✨ Créer une session
              </button>
            </div>
          </div>
        )}

        {filteredSessions.length > 0 && (
          <>
            <div className="sessions-grid">
              {paginatedSessions.map(session => (
                <SessionTile 
                  key={session.session_id}
                  session={session}
                  onClick={handleSessionClick}
                />
              ))}
            </div>
            {renderPaginationButtons()}
          </>
        )}

        {!error && sessions.length > 0 && (
          <div className="text-center mt-4">
            <p className="text-xs text-secondary">
              🔄 Mise à jour automatique toutes les 5 secondes
            </p>
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.user.id === nextProps.user.id;
});

SessionSelector.displayName = 'SessionSelector';

export default SessionSelector;